// Language resolver. Re-exports the correct dataset based on the URL language.
// Language switching is a full navigation (see Navbar), so this evaluates once
// per page load with the right language.
import * as en from "./site.en";
import * as el from "./site.el";
import { LANG } from "@/lib/lang";

const C = LANG === "el" ? el : en;

export const site = C.site;
export const nav = C.nav;
export const home = C.home;
export const about = C.about;
export const apartments = C.apartments;
export const heritage = C.heritage;
export const amenities = C.amenities;
export const houseRules = C.houseRules;
export const discover = C.discover;
export const galleryImages = C.galleryImages;
export const policy = C.policy;
export const bookingPolicy = C.bookingPolicy;

// Hardcoded UI microcopy (kept out of the page data files).
const UI = {
  en: {
    contactUs: "Contact Us",
    accommodation: "Accommodation",
    bookingPolicy: "Booking Policy",
    language: "Language",
    reviewsHeading: "What Our Guests Say",
    reviewsSub: "Real stays, in our guests' own words.",
    legal: "Legal",
    payment: "Payment",
    cancellation: "Cancellation",
    home: "Home",
    menu: "Menu",
    guides: "Guides",
    guests: "Guests",
    size: "Size",
    beds: "Beds",
    bathroom: "Bathroom",
    atAGlance: "At a Glance",
    homeTitle: "Boutique Apartments in Trypiti, Milos",
    homeDesc: "A collection of boutique apartments inside a restored 18th-century stone house in Trypiti, Milos — terrace views over the Aegean, minutes from Plaka, Klima and the port at Adamas.",
    aboutDesc: "The story of Efi's Apartments — an 18th-century, state-protected Cycladic stone house in Trypiti, Milos, restored and lived-in, with three centuries of village life kept intact.",
    galleryTitle: "Gallery",
    galleryDesc: "A visual tour of Efi's Apartments and the island of Milos — the restored stone interiors, terraces and the colours of the Aegean.",
    discoverTitle: "Discover Milos — The Island Guide",
    discoverDesc: "How to make the most of Milos from Trypiti — the lay of the land, getting around, the spots near Efi's Apartments, and Efi's own island guides.",
    guidesTitle: "Travel Guides to Milos",
    guidesDesc: "Efi's own travel guides to Milos — the best beaches, boat trips, sunsets, villages, water activities and ancient sites, written by a local host since 1995.",
    contactTitle: "Contact & Directions",
    contactDesc: "Get in touch with Efi's Apartments in Trypiti, Milos — phone, WhatsApp, email and directions. Perched above Klima, minutes from Plaka and the port at Adamas.",
    guidesEyebrow: "Travel Guides",
    guidesHeroTitle: "Your Guide to Milos",
    guidesHeroSub: "Everything worth knowing about Milos — the beaches, the villages, the food, and the spots that don't make it onto most maps.",
    efiHostSig: "Efi, your host in Trypiti",
    contactEyebrow: "Contact",
    contactHeroTitle: "Find Us in Trypiti",
    contactHeroSub: "Perched above Klima, a few minutes from Plaka and the port at Adamas. Reach out — Efi will send exact directions when you book.",
    labelPhone: "Phone",
    labelEmail: "Email",
    labelAddress: "Address",
    howToTitle: "How to Get Here",
    howToText: "Trypiti sits on the hill above Klima, roughly ten minutes from Adamas port and five from Plaka. The village roads are narrow: once you see the volcanic stone walls, you're in the right place. Efi will send you exact directions when you book.",
    takeMeThere: "Take Me There",
    ctaEyebrow: "Trypiti, Milos",
    ctaA: "Your stay above",
    ctaB: "the ",
    ctaAegean: "Aegean",
    ctaC: " awaits.",
    footerDesc: "A small collection of apartments inside a restored 18th-century stone house in Trypiti, Milos.",
    explore: "Explore",
    getInTouch: "Get in Touch",
    privacyPolicy: "Privacy Policy",
    allRights: "All rights reserved.",
    designedBy: "Designed & Developed by",
    galleryEyebrow: "Gallery",
    gallerySeeTitle: "See for Yourself",
    tabMilos: "Milos",
    tabApartments: "Apartments",
    localTime: "local",
    fetchingWeather: "Fetching weather…",
    homeIntro: { a: "An ", i1: "18th-century", b: " stone house in ", i2: "Trypiti", c: ", restored and lived-in, with a terrace view over the Aegean that sets the tone for everything." },
    ownerLabel: "— Efi, Owner",
    aboutEyebrow: "House History",
    aboutSweep: "Untouched by time",
    aboutSweepSub: "Three hundred years of Cycladic life, kept exactly as it was — and quietly made yours.",
    aboutStoryPill: "The Story",
    aboutStoryTitle: "Scroll through the centuries.",
    aboutBigCaption: "Volcanic stone, real wood, centuries of stories.",
    aboutDetailsPill: "In the Details",
    aboutDetailsTitle: "The little things, kept on purpose.",
    aboutDetailsText: "Stone ovens, rainwater cisterns, grape presses and laundry stones — the everyday objects of village life, preserved throughout the house.",
    aboutSeeMore: "There's far more to see in person.",
    viewFullGallery: "View the Full Gallery",
    aboutCtaTitle: "Sleep inside the history.",
    aboutCtaText: "Each apartment preserves its own piece of the past — choose the one that speaks to you.",
    discoverHeroEyebrow: "The Island",
    discoverIntro: { a: "Milos doesn't reveal itself all at once. Give it ", i1: "a few slow days", b: " and it quietly becomes the island you ", i2: "compare every other one to." },
    practicalities: "Practicalities",
    dragHint: "Drag left or right to explore the nearest spots",
    featuredGuide: "Featured Guide",
    readTheGuide: "Read the guide",
    weather: { clear: "Clear sky", partly: "Partly cloudy", overcast: "Overcast", fog: "Foggy", drizzle: "Drizzle", rain: "Rain", snow: "Snow", storm: "Thunderstorm" },
    exploreBtn: "Explore",
    readGuide: "Read guide",
    discoverMilosBtn: "Discover Milos",
    upTo: "Up to",
    heritageHeading: { a: "A house the centuries ", i: "left untouched." },
    ourAmenities: "OUR AMENITIES",
    ourApartments: "OUR APARTMENTS",
    closerLook: "A Closer Look",
    viewGallery: "View Gallery",
    slowMorningsTitle: "Made for slow mornings",
    slowMorningsText: "Our apartments are intentionally not full kitchens — each is thoughtfully equipped with everything you need for a lovely breakfast and light snacks.",
    protectedHouse: "Protected 18th-century house",
    readHouseStory: "Read the House Story",
    goodToKnow: "Good to Know",
    yourStayTitle: "Your Stay, Simply Put",
    yourStayText: "Everything you need to know before and during your time at Efi's.",
    paymentCancellation: "Payment & Cancellation",
    paymentCancellationText: "Two simple rate types. Read the full payment and cancellation terms before you book.",
    moreApartments: "More Apartments",
    livingHistory: "A Piece of Living History",
    restoredNotReinvented: "Restored, Not Reinvented",
    pleaseNote: "Please note —",
    localTip: "Local tip — ",
    allGuides: "All Guides",
    makeTrypitiBase: "Make Trypiti your base.",
    makeTrypitiText: "Stay at Efi's and let our experience since 1995 shape your days on Milos.",
    exploreMoreGuides: "Explore More Guides",
    dragToExplore: "Drag to explore →",
  },
  el: {
    contactUs: "Επικοινωνία",
    accommodation: "Καταλύματα",
    bookingPolicy: "Πολιτική Κρατήσεων",
    language: "Γλώσσα",
    reviewsHeading: "Τι Λένε οι Επισκέπτες μας",
    reviewsSub: "Πραγματικές διαμονές, με τα λόγια των ίδιων των επισκεπτών μας.",
    legal: "Νομικά",
    payment: "Πληρωμή",
    cancellation: "Ακύρωση",
    home: "Αρχική",
    menu: "Μενού",
    guides: "Οδηγοί",
    guests: "Άτομα",
    size: "Μέγεθος",
    beds: "Κρεβάτια",
    bathroom: "Μπάνιο",
    atAGlance: "Με μια Ματιά",
    homeTitle: "Μπουτίκ Διαμερίσματα στην Τρυπητή, Μήλος",
    homeDesc: "Μια συλλογή μπουτίκ διαμερισμάτων μέσα σε αναστηλωμένο πέτρινο σπίτι του 18ου αιώνα στην Τρυπητή της Μήλου — θέα στο Αιγαίο από τη βεράντα, λίγα λεπτά από την Πλάκα, το Κλήμα και το λιμάνι στον Αδάμαντα.",
    aboutDesc: "Η ιστορία των Efi's Apartments — ένα προστατευόμενο κυκλαδίτικο πέτρινο σπίτι του 18ου αιώνα στην Τρυπητή της Μήλου, αναστηλωμένο και ζωντανό, με τρεις αιώνες ζωής του χωριού να μένουν ανέγγιχτοι.",
    galleryTitle: "Φωτογραφίες",
    galleryDesc: "Μια εικονική περιήγηση στα Efi's Apartments και στο νησί της Μήλου — τα αναστηλωμένα πέτρινα εσωτερικά, οι βεράντες και τα χρώματα του Αιγαίου.",
    discoverTitle: "Ανακαλύψτε τη Μήλο — Ο Οδηγός του Νησιού",
    discoverDesc: "Πώς να απολαύσετε τη Μήλο με βάση την Τρυπητή — η διαμόρφωση του τόπου, οι μετακινήσεις, τα σημεία κοντά στα Efi's Apartments και οι προσωπικοί οδηγοί της Έφης.",
    guidesTitle: "Ταξιδιωτικοί Οδηγοί για τη Μήλο",
    guidesDesc: "Οι προσωπικοί ταξιδιωτικοί οδηγοί της Έφης για τη Μήλο — οι καλύτερες παραλίες, εκδρομές με βάρκα, ηλιοβασιλέματα, χωριά, θαλάσσιες δραστηριότητες και αρχαία μνημεία, γραμμένοι από ντόπια οικοδέσποινα από το 1995.",
    contactTitle: "Επικοινωνία & Πρόσβαση",
    contactDesc: "Επικοινωνήστε με τα Efi's Apartments στην Τρυπητή της Μήλου — τηλέφωνο, WhatsApp, email και οδηγίες πρόσβασης. Πάνω από το Κλήμα, λίγα λεπτά από την Πλάκα και το λιμάνι στον Αδάμαντα.",
    guidesEyebrow: "Ταξιδιωτικοί Οδηγοί",
    guidesHeroTitle: "Ο Οδηγός σας για τη Μήλο",
    guidesHeroSub: "Όλα όσα αξίζει να ξέρετε για τη Μήλο — οι παραλίες, τα χωριά, το φαγητό, και οι γωνιές που δεν φτάνουν στους περισσότερους χάρτες.",
    efiHostSig: "Έφη, η οικοδέσποινά σας στην Τρυπητή",
    contactEyebrow: "Επικοινωνία",
    contactHeroTitle: "Βρείτε μας στην Τρυπητή",
    contactHeroSub: "Πάνω από το Κλήμα, λίγα λεπτά από την Πλάκα και το λιμάνι στον Αδάμαντα. Επικοινωνήστε — η Έφη θα σας στείλει ακριβείς οδηγίες μόλις κλείσετε.",
    labelPhone: "Τηλέφωνο",
    labelEmail: "Email",
    labelAddress: "Διεύθυνση",
    howToTitle: "Πώς θα Έρθετε",
    howToText: "Η Τρυπητή βρίσκεται στον λόφο πάνω από το Κλήμα, περίπου δέκα λεπτά από το λιμάνι του Αδάμαντα και πέντε από την Πλάκα. Τα δρομάκια του χωριού είναι στενά: μόλις δείτε τους τοίχους από ηφαιστειακή πέτρα, βρίσκεστε στο σωστό σημείο. Η Έφη θα σας στείλει ακριβείς οδηγίες μόλις κλείσετε.",
    takeMeThere: "Οδηγήστε με Εκεί",
    ctaEyebrow: "Τρυπητή, Μήλος",
    ctaA: "Η διαμονή σας πάνω",
    ctaB: "από το ",
    ctaAegean: "Αιγαίο",
    ctaC: " περιμένει.",
    footerDesc: "Μια μικρή συλλογή διαμερισμάτων μέσα σε αναστηλωμένο πέτρινο σπίτι του 18ου αιώνα στην Τρυπητή της Μήλου.",
    explore: "Εξερευνήστε",
    getInTouch: "Επικοινωνία",
    privacyPolicy: "Πολιτική Απορρήτου",
    allRights: "Με επιφύλαξη παντός δικαιώματος.",
    designedBy: "Σχεδιασμός & Ανάπτυξη από",
    galleryEyebrow: "Φωτογραφίες",
    gallerySeeTitle: "Δείτε με τα Μάτια σας",
    tabMilos: "Μήλος",
    tabApartments: "Διαμερίσματα",
    localTime: "τοπική",
    fetchingWeather: "Λήψη καιρού…",
    homeIntro: { a: "Ένα πέτρινο σπίτι ", i1: "του 18ου αιώνα", b: " στην ", i2: "Τρυπητή", c: ", αναστηλωμένο και ζωντανό, με θέα από τη βεράντα στο Αιγαίο που δίνει τον τόνο σε όλα." },
    ownerLabel: "— Έφη, Ιδιοκτήτρια",
    aboutEyebrow: "Ιστορία του Σπιτιού",
    aboutSweep: "Ανέγγιχτο από τον χρόνο",
    aboutSweepSub: "Τριακόσια χρόνια κυκλαδίτικης ζωής, διατηρημένα ακριβώς όπως ήταν — και διακριτικά δικά σας.",
    aboutStoryPill: "Η Ιστορία",
    aboutStoryTitle: "Ταξιδέψτε μέσα στους αιώνες.",
    aboutBigCaption: "Ηφαιστειακή πέτρα, αληθινό ξύλο, αιώνες ιστοριών.",
    aboutDetailsPill: "Στη Λεπτομέρεια",
    aboutDetailsTitle: "Οι μικρές λεπτομέρειες, διατηρημένες σκόπιμα.",
    aboutDetailsText: "Πέτρινοι φούρνοι, στέρνες για το νερό της βροχής, πατητήρια και πέτρες πλυσίματος — τα καθημερινά αντικείμενα της ζωής του χωριού, διατηρημένα σε όλο το σπίτι.",
    aboutSeeMore: "Υπάρχουν πολλά περισσότερα να δείτε από κοντά.",
    viewFullGallery: "Δείτε Όλες τις Φωτογραφίες",
    aboutCtaTitle: "Κοιμηθείτε μέσα στην ιστορία.",
    aboutCtaText: "Κάθε διαμέρισμα διατηρεί το δικό του κομμάτι του παρελθόντος — διαλέξτε αυτό που σας μιλάει.",
    discoverHeroEyebrow: "Το Νησί",
    discoverIntro: { a: "Η Μήλος δεν αποκαλύπτεται με τη μία. Δώστε της ", i1: "λίγες αργές μέρες", b: " και σιωπηλά γίνεται το νησί με το οποίο ", i2: "συγκρίνετε κάθε άλλο." },
    practicalities: "Πρακτικά",
    dragHint: "Σύρετε αριστερά ή δεξιά για να εξερευνήσετε τα κοντινότερα σημεία",
    featuredGuide: "Επιλεγμένος Οδηγός",
    readTheGuide: "Διαβάστε τον οδηγό",
    weather: { clear: "Αίθριος", partly: "Λίγα σύννεφα", overcast: "Συννεφιά", fog: "Ομίχλη", drizzle: "Ψιχάλα", rain: "Βροχή", snow: "Χιόνι", storm: "Καταιγίδα" },
    exploreBtn: "Εξερεύνηση",
    readGuide: "Διαβάστε τον οδηγό",
    discoverMilosBtn: "Ανακαλύψτε τη Μήλο",
    upTo: "Έως",
    heritageHeading: { a: "Ένα σπίτι που οι αιώνες ", i: "άφησαν ανέγγιχτο." },
    ourAmenities: "ΟΙ ΠΑΡΟΧΕΣ ΜΑΣ",
    ourApartments: "ΤΑ ΔΙΑΜΕΡΙΣΜΑΤΑ ΜΑΣ",
    closerLook: "Μια Πιο Κοντινή Ματιά",
    viewGallery: "Δείτε τη Συλλογή",
    slowMorningsTitle: "Φτιαγμένο για χαλαρά πρωινά",
    slowMorningsText: "Τα διαμερίσματά μας σκόπιμα δεν διαθέτουν πλήρεις κουζίνες — καθένα είναι προσεκτικά εξοπλισμένο με ό,τι χρειάζεστε για ένα υπέροχο πρωινό και ελαφριά σνακ.",
    protectedHouse: "Προστατευόμενο σπίτι του 18ου αιώνα",
    readHouseStory: "Διαβάστε την Ιστορία του Σπιτιού",
    goodToKnow: "Καλό να Ξέρετε",
    yourStayTitle: "Η Διαμονή σας, με Απλά Λόγια",
    yourStayText: "Όλα όσα πρέπει να ξέρετε πριν και κατά τη διάρκεια της διαμονής σας στης Έφης.",
    paymentCancellation: "Πληρωμή & Ακύρωση",
    paymentCancellationText: "Δύο απλοί τύποι τιμών. Διαβάστε τους πλήρεις όρους πληρωμής και ακύρωσης πριν κλείσετε.",
    moreApartments: "Περισσότερα Διαμερίσματα",
    livingHistory: "Ένα Κομμάτι Ζωντανής Ιστορίας",
    restoredNotReinvented: "Αναστηλωμένο, Όχι Ανακαινισμένο",
    pleaseNote: "Σημειώστε —",
    localTip: "Τοπική συμβουλή — ",
    allGuides: "Όλοι οι Οδηγοί",
    makeTrypitiBase: "Κάντε την Τρυπητή βάση σας.",
    makeTrypitiText: "Μείνετε στης Έφης και αφήστε την εμπειρία μας από το 1995 να διαμορφώσει τις μέρες σας στη Μήλο.",
    exploreMoreGuides: "Εξερευνήστε Περισσότερους Οδηγούς",
    dragToExplore: "Σύρετε για εξερεύνηση →",
  },
};

export const ui = UI[LANG];
export { LANG };
